import axios, { ParamsSerializerOptions } from 'axios';
import qs from 'qs';

type IApiVariants =
  | 'analytics'
  | 'auth'
  | 'cms'
  | 'customization'
  | 'dictionary'
  | 'googleFonts'
  | 'integrationBuilder'
  | 'kibana'
  | 'logo'
  | 'main'
  | 'notifications';

export const getDomains = () => {
  const { hostname } = window.location;
  const [subDomain = '', secondLevelDomain = '', topLevelDomain = ''] =
    hostname.split('.');

  return {
    secondLevelDomain,
    subDomain,
    topLevelDomain,
  };
};

export const getApiUrl = <T extends IApiVariants>(variant: T) => {
  const { secondLevelDomain, topLevelDomain } = getDomains();

  const rootDomain = [secondLevelDomain, topLevelDomain].join('.');

  const isCashir = secondLevelDomain === 'cashir';

  let cmsRootDomain = rootDomain;
  if (isCashir) {
    /*
      We have only prod CMS instance and our DEV/BEST envs have to go to prod CMS
      regardless of the current env. And this should be done only for Cashir.
    */
    cmsRootDomain = [secondLevelDomain, 'live'].join('.');
  }

  let apiVariants = {
    analytics: `https://${global?.CONFIG?.ANALYTICS_SUBDOMAIN}.${rootDomain}/api/v1/`,
    auth: `https://${global?.CONFIG?.AUTH_SUBDOMAIN}.${rootDomain}/`,
    cms: `https://${global?.CONFIG?.CMS_SUBDOMAIN}.${cmsRootDomain}/api/`,
    customization: `https://${global?.CONFIG?.CUSTOMIZATION_SUBDOMAIN}.${rootDomain}/api/`,
    dictionary: `https://${global?.CONFIG?.DICTIONARY_SUBDOMAIN}.${rootDomain}/public/api/v1/`,
    googleFonts: `https://${global?.CONFIG?.GOOGLE_FONTS_SUBDOMAIN}.${rootDomain}/`,
    integrationBuilder: `https://${global?.CONFIG?.INTEGRATION_BUILDER_SUBDOMAIN}.${rootDomain}/public/api/v1/`,
    kibana: `${global?.CONFIG?.KIBANA_HREF_TEMPLATE}`,
    logo: (name: string) =>
      `https://${global?.CONFIG?.IMAGES_SUBDOMAIN}.${rootDomain}/${name}`,
    main: `https://${global?.CONFIG?.MAIN_SUBDOMAIN}.${rootDomain}/public/api/v1/`,
    notifications: `https://${global?.CONFIG?.NOTIFICATIONS_SUBDOMAIN}.${rootDomain}/api/v1/`,
  };

  if (process.env.NODE_ENV !== 'production') {
    apiVariants = {
      analytics: import.meta.env.VITE_ANALYTICS_URL || '',
      auth: import.meta.env.VITE_AUTH_URL || '',
      cms: import.meta.env.VITE_CMS_URL || '',
      customization: import.meta.env.VITE_CUSTOMIZATION_SERVICE_URL_LOCAL || '',
      dictionary: import.meta.env.VITE_DICTIONARY_URL || '',
      googleFonts: import.meta.env.VITE_FONTS_URL,
      integrationBuilder: import.meta.env.VITE_INTEGRATION_BUILDER_SUBDOMAIN || '',
      kibana: import.meta.env.VITE_KIBANA_HREF_TEMPLATE || '',
      logo: (name: string) => `${import.meta.env.VITE_LOGO_IMAGES_DEV || ''}${name}`,
      main: import.meta.env.VITE_API_URL,
      notifications: import.meta.env.VITE_NOTIFICATIONS_URL,
    };
  }

  return apiVariants[variant];
};

const paramsSerializer: ParamsSerializerOptions = {
  serialize: (params) => {
    return qs.stringify(params, { arrayFormat: 'brackets' });
  },
};

// For endpoints that bind a comma-separated list — the default emits `key[]` pairs.
export const commaParamsSerializer = {
  serialize: (params: unknown) => {
    return qs.stringify(params, { arrayFormat: 'comma' });
  },
};

export const TIMEOUT_ERROR_MESSAGE = 'notifications.longResponse';

const axiosInstance = axios.create({
  baseURL: getApiUrl('main') || '',
  paramsSerializer,
  timeout: 60 * 1000,
  timeoutErrorMessage: TIMEOUT_ERROR_MESSAGE,
});

export const builderInstance = axios.create({
  baseURL: getApiUrl('integrationBuilder') || '',
  paramsSerializer,
  timeout: 60 * 1000,
  timeoutErrorMessage: TIMEOUT_ERROR_MESSAGE,
});

export const dictionaryInstance = axios.create({
  baseURL: getApiUrl('dictionary') || '',
  paramsSerializer,
  timeout: 60 * 1000,
  timeoutErrorMessage: TIMEOUT_ERROR_MESSAGE,
});

export const axiosAuthInstance = axios.create({
  baseURL: getApiUrl('auth') || '',
  paramsSerializer,
  timeout: 60 * 1000,
  timeoutErrorMessage: TIMEOUT_ERROR_MESSAGE,
});

export const customizationInstance = axios.create({
  baseURL: getApiUrl('customization') || '',
  timeout: 60 * 1000,
  timeoutErrorMessage: TIMEOUT_ERROR_MESSAGE,
});

export const cmsInstance = axios.create({
  baseURL: getApiUrl('cms') || '',
  timeout: 60 * 1000,
  timeoutErrorMessage: TIMEOUT_ERROR_MESSAGE,
});

export const analyticsInstance = axios.create({
  baseURL: getApiUrl('analytics') || '',
  paramsSerializer,
  timeout: 60 * 1000,
  timeoutErrorMessage: TIMEOUT_ERROR_MESSAGE,
});

export const notificationsInstance = axios.create({
  baseURL: getApiUrl('notifications') || '',
  paramsSerializer,
  timeout: 60 * 1000,
  timeoutErrorMessage: TIMEOUT_ERROR_MESSAGE,
});

export const fontsApiUrl = `${getApiUrl('googleFonts')}fonts/`;

export const googleFontsAxiosInstance = axios.create({
  baseURL: fontsApiUrl,
  paramsSerializer,
  timeout: 60 * 1000,
  timeoutErrorMessage: TIMEOUT_ERROR_MESSAGE,
});

// Instances that carry the session bearer token — header rotation and auth interceptors apply to exactly these
export const TOKEN_INSTANCES = [
  axiosInstance,
  axiosAuthInstance,
  analyticsInstance,
  notificationsInstance,
  builderInstance,
  dictionaryInstance,
];

export default axiosInstance;
